import React, {useCallback, useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Grid,
    Paper,
    Stack,
    Switch,
    TableContainer,
    Tooltip,
    Typography,
} from "@mui/material";
import {DataGrid, GridActionsCellItem, GridColDef, GridRowParams, GridToolbarQuickFilter} from "@mui/x-data-grid";
import {format, parseISO} from "date-fns";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleCheck, faEnvelope, faRotateRight} from "@fortawesome/pro-solid-svg-icons";
import {useTranslation} from "react-i18next";
import {useApplicationContext} from "../../ApplicationContext";
import {useDocumentTitle} from "../../Hooks/useDocumentTitle";
import {RegistrationFollowUpDto} from "../../apiClient/Api";

/**
 * Vertriebs-Übersicht aller Registrierungs-Follow-Ups: zeigt offene Anfragen
 * (per Default) oder alle inkl. bestätigter. Erlaubt Acknowledge direkt aus
 * der Liste — was den Klick auf den Mail-Link spart, wenn der Vertrieb die
 * Anfrage bereits bearbeitet hat.
 */
export const AdminRegistrationFollowUps: React.FC = () => {
    useDocumentTitle("registrationFollowUps.title");
    const context = useApplicationContext();
    const {t} = useTranslation();

    const [followUps, setFollowUps] = useState<RegistrationFollowUpDto[]>([]);
    const [isFetching, setIsFetching] = useState(false);
    const [includeAcknowledged, setIncludeAcknowledged] = useState(false);
    const [confirmAckId, setConfirmAckId] = useState<string | undefined>(undefined);

    const load = useCallback(() => {
        setIsFetching(true);
        context.api.user
            .userListRegistrationFollowUps({includeAcknowledged})
            .then(r => setFollowUps(r.data ?? []))
            .catch(context.apiError)
            .finally(() => setIsFetching(false));
    }, [context.api, context.apiError, includeAcknowledged]);

    useEffect(() => {
        load();
    }, [load]);

    const acknowledge = (id: string) => {
        context.api.user
            .userAcknowledgeRegistration(id)
            .then(() => load())
            .catch(context.apiError);
    };

    const formatDateTime = (iso: string | null | undefined): string => {
        if (!iso) return "";
        return format(parseISO(iso), "dd.MM.yyyy HH:mm");
    };

    const columns: GridColDef<RegistrationFollowUpDto>[] = [
        {
            field: "createdAt",
            headerName: t("registrationFollowUps.columns.createdAt"),
            width: 160,
            valueFormatter: (value: string | undefined) => formatDateTime(value ?? null),
        },
        {field: "customer", headerName: t("registrationFollowUps.columns.customer"), flex: 1, minWidth: 180},
        {
            field: "name",
            headerName: t("registrationFollowUps.columns.name"),
            flex: 1,
            minWidth: 160,
            valueGetter: (_value: any, row: RegistrationFollowUpDto) =>
                `${row.firstName} ${row.lastName}`.trim(),
        },
        {
            field: "email",
            headerName: t("registrationFollowUps.columns.email"),
            flex: 1,
            minWidth: 200,
            renderCell: params => (
                <Button
                    size="small"
                    href={`mailto:${params.row.email}`}
                    startIcon={<FontAwesomeIcon icon={faEnvelope}/>}
                    sx={{textTransform: "none"}}
                >
                    {params.row.email}
                </Button>
            ),
        },
        {
            field: "supportContractId",
            headerName: t("registrationFollowUps.columns.supportContractId"),
            width: 160,
            valueFormatter: (value: string | null | undefined) => value ?? "-",
        },
        {
            field: "status",
            headerName: t("registrationFollowUps.columns.status"),
            width: 220,
            renderCell: params => {
                const row = params.row;
                if (row.acknowledgedAt) {
                    return (
                        <Tooltip
                            title={t("registrationFollowUps.acknowledgedTooltip", {
                                by: row.acknowledgedByEmail ?? "—",
                                at: formatDateTime(row.acknowledgedAt),
                            })}
                        >
                            <Chip
                                size="small"
                                color="success"
                                icon={<FontAwesomeIcon icon={faCircleCheck}/>}
                                label={t("registrationFollowUps.status.acknowledged")}
                            />
                        </Tooltip>
                    );
                }
                if (row.reminderSentAt) {
                    return (
                        <Tooltip title={t("registrationFollowUps.reminderTooltip", {at: formatDateTime(row.reminderSentAt)})}>
                            <Chip
                                size="small"
                                color="warning"
                                icon={<FontAwesomeIcon icon={faRotateRight}/>}
                                label={t("registrationFollowUps.status.reminded")}
                            />
                        </Tooltip>
                    );
                }
                return (
                    <Chip
                        size="small"
                        color="info"
                        label={t("registrationFollowUps.status.open")}
                    />
                );
            },
        },
        {
            field: "actions",
            type: "actions",
            width: 80,
            getActions: (params: GridRowParams<RegistrationFollowUpDto>) => {
                if (params.row.acknowledgedAt) return [];
                return [
                    <GridActionsCellItem
                        key="ack"
                        icon={
                            <Tooltip title={t("registrationFollowUps.actions.acknowledge")}>
                                <FontAwesomeIcon icon={faCircleCheck}/>
                            </Tooltip>
                        }
                        label={t("registrationFollowUps.actions.acknowledge")}
                        onClick={() => setConfirmAckId(params.row.id)}
                        showInMenu={false}
                    />,
                ];
            },
        },
    ];

    const Toolbar = () => (
        <Grid container sx={{p: 2, alignItems: "center"}}>
            <Grid size={{xs: 12, md: 8}}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={includeAcknowledged}
                            onChange={e => setIncludeAcknowledged(e.target.checked)}
                        />
                    }
                    label={t("registrationFollowUps.includeAcknowledged")}
                />
            </Grid>
            <Grid size={{xs: 12, md: 4}}>
                <Stack direction="row" sx={{justifyContent: "flex-end"}}>
                    <GridToolbarQuickFilter/>
                </Stack>
            </Grid>
        </Grid>
    );

    return (
        <Box sx={{width: "100%"}}>
            <Stack direction="row" sx={{alignItems: "center", justifyContent: "space-between", mb: 2}}>
                <Typography variant="h5">{t("registrationFollowUps.title")}</Typography>
                <Button onClick={load} startIcon={<FontAwesomeIcon icon={faRotateRight}/>} variant="outlined">
                    {t("registrationFollowUps.refresh")}
                </Button>
            </Stack>

            {!isFetching && followUps.length === 0 && (
                <Alert severity="info" sx={{mb: 2}}>
                    {includeAcknowledged
                        ? t("registrationFollowUps.emptyAll")
                        : t("registrationFollowUps.emptyOpen")}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <DataGrid
                    rows={followUps}
                    columns={columns}
                    loading={isFetching}
                    autoHeight
                    getRowId={row => row.id}
                    slots={{toolbar: Toolbar}}
                    initialState={{
                        sorting: {sortModel: [{field: "createdAt", sort: "desc"}]},
                    }}
                />
            </TableContainer>

            <Dialog open={confirmAckId !== undefined} onClose={() => setConfirmAckId(undefined)}>
                <DialogTitle>{t("registrationFollowUps.confirmAck.title")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("registrationFollowUps.confirmAck.body")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmAckId(undefined)} color="inherit">
                        {t("registrationFollowUps.confirmAck.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            if (confirmAckId) acknowledge(confirmAckId);
                            setConfirmAckId(undefined);
                        }}
                    >
                        {t("registrationFollowUps.confirmAck.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminRegistrationFollowUps;
